// 進行データ(発行/出荷/搬入/施工等)へのデータリンクと、
// マスタ閲覧(工事/材料/工種/配合比等)へのマスタリンクをまとめる。
// 視覚的に区別するため色を分ける: データ=青系、マスタ=緑系。

import { Link } from "react-router-dom";
import { JSX, ReactNode, MouseEventHandler } from "react";

const DATA_LINK_STYLE: React.CSSProperties = {
  color: "#1976d2",
  textDecoration: "underline",
};

const MASTER_LINK_STYLE: React.CSSProperties = {
  color: "#2e7d32",
  textDecoration: "underline",
};

// UUID / プレフィクス付きID / 整数ID を表示用の十進数文字列に変換する。
export function formatEntityId(id: string | number | undefined | null): string {
  if (id == null || id === "") return "";
  if (typeof id === "number") return String(id);
  const s = String(id);

  // UUID: 5 セグメント、全て hex
  const parts = s.split("-");
  if (
    parts.length === 5 &&
    parts.every((p) => p.length > 0 && /^[0-9a-fA-F]+$/.test(p))
  ) {
    const lastSegment = parts[4] ?? "";
    try {
      return BigInt("0x" + lastSegment).toString(10);
    } catch {
      return lastSegment;
    }
  }

  const m = s.match(/(\d+)$/);
  if (m) {
    const n = (m[1] ?? "").replace(/^0+/, "") || "0";
    return n;
  }

  return s;
}

// =============================================================================
// データリンク
// =============================================================================

export type DataEntityType =
  | "issuance"
  | "carry-out"
  | "lot"
  | "carry-in"
  | "plan-area"
  | "construction"
  | "material-qr";

type DataEntityDef = {
  label: string;
  path: (id: string | number, parentId?: string | number) => string;
};

const DATA_ENTITY_DEFS: Record<DataEntityType, DataEntityDef> = {
  "issuance":     { label: "発行",       path: (id) => `/console/issuance/${id}/` },
  "carry-out":    { label: "出荷",       path: (id) => `/console/carry-out/${id}/` },
  "lot":          { label: "材料ロット", path: (id, parent) => `/console/carry-out/${parent ?? ""}/lot/${id}/` },
  "carry-in":     { label: "搬入",       path: (id) => `/console/carry-in/${id}/` },
  "plan-area":    { label: "施工箇所",   path: (id) => `/console/construction-plan/area/${id}/` },
  "construction": { label: "施工実施",   path: (id) => `/console/construction-execute/${id}/` },
  "material-qr":  { label: "材料",       path: (id) => `/console/search-materials/${id}/` },
};

export function dataEntityLabel(type: DataEntityType): string {
  return DATA_ENTITY_DEFS[type].label;
}

export function dataEntityHref(type: DataEntityType, id: string | number, parentId?: string | number): string {
  return DATA_ENTITY_DEFS[type].path(id, parentId);
}

export function dataEntityText(type: DataEntityType, id: string | number): string {
  return `${DATA_ENTITY_DEFS[type].label} ${formatEntityId(id)}`;
}

export type DataLinkProps = {
  type: DataEntityType;
  id: string | number;
  parentId?: string | number;
  label?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  style?: React.CSSProperties;
};

export function DataLink({ type, id, parentId, label, onClick, style }: DataLinkProps): JSX.Element {
  const def = DATA_ENTITY_DEFS[type];
  const href = def.path(id, parentId);
  const display = formatEntityId(id);
  return (
    <Link to={href} onClick={onClick} className="data-link" style={{ ...DATA_LINK_STYLE, ...(style ?? {}) }}>
      {label ?? def.label} {display}
    </Link>
  );
}

// =============================================================================
// マスタリンク
// =============================================================================

export type MasterEntityType =
  | "work"
  | "area-division"
  | "material-division"
  | "client"
  | "work-type"
  | "work-subtype"
  | "work-detail"
  | "construction-method"
  | "stirring-method"
  | "carry-in-location"
  | "location-division"
  | "quality-measurement-item"
  | "material"
  | "material-detail"
  | "pot-life"
  | "mix-ratio";

export function masterEntityHref(type: MasterEntityType, id: number | string): string {
  return `/console/master-view/${type}/${id}/`;
}

export type MasterLinkProps = {
  type: MasterEntityType;
  id: number | string | null | undefined;
  name: ReactNode;
  fallback?: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  style?: React.CSSProperties;
};

export function MasterLink({ type, id, name, fallback, onClick, style }: MasterLinkProps): JSX.Element {
  if (id == null) {
    return <>{fallback ?? "-"}</>;
  }
  return (
    <Link to={masterEntityHref(type, id)} onClick={onClick} className="master-link" style={{ ...MASTER_LINK_STYLE, ...(style ?? {}) }}>
      {name}
    </Link>
  );
}
